import { useMemo, useState } from 'react';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyStateNoProfile } from '../components/empty-state-no-profile';
import { PeriodFilter } from '../components/period-filter';
import { SectionDivider } from '../components/section-divider';
import { SuggestedOpportunityCard } from '../components/suggested-opportunity-card';
import {
  useRecomputeSuggestions,
  useSuggestedOpportunities,
  useSuggestedStats,
} from '../hooks/use-suggested-opportunities';
import type { SuggestedOpportunity } from '../types/suggested-opportunities.types';

/**
 * Sprint 5b.1.5 v2 — Page « Opportunités suggérées ».
 *
 * Affiche les 3 sections Forte/Modérée/Faible séparées visuellement. Section Faible
 * repliable par défaut. Bouton « Recalculer mes suggestions » en en-tête (amendement v2.1 B.1).
 *
 * Empty state pédagogique si `total === 0 && stats encore non chargées` ou si l'utilisateur
 * n'a pas configuré son profil commercial (cf. décision #11 — sourcing désactivé côté backend).
 */
export function SuggestedOpportunitiesPage() {
  // Sprint 5b.1.6 A.4.b — filtre période piloté par état local, défaut 7j (cron-aligned).
  const [periodDays, setPeriodDays] = useState<7 | 14 | 30>(7);
  const filters = useMemo(() => ({ periodDays }), [periodDays]);

  const { data: stats, isLoading: statsLoading } = useSuggestedStats(filters);
  const { data: suggestions, isLoading: listLoading } = useSuggestedOpportunities(filters);
  const recompute = useRecomputeSuggestions();

  const grouped = useMemo(() => {
    const empty: {
      strong: SuggestedOpportunity[];
      moderate: SuggestedOpportunity[];
      weak: SuggestedOpportunity[];
    } = { strong: [], moderate: [], weak: [] };
    if (!suggestions) return empty;
    for (const ao of suggestions) {
      const s = ao.autoSuggestionScore;
      if (s === null) empty.weak.push(ao);
      else if (s >= 70) empty.strong.push(ao);
      else if (s >= 40) empty.moderate.push(ao);
      else empty.weak.push(ao);
    }
    return empty;
  }, [suggestions]);

  if (statsLoading || listLoading) {
    return (
      <div className="space-y-4 max-w-5xl mx-auto">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const isEmpty = !suggestions || suggestions.length === 0;
  const isFiltered = periodDays !== 7;

  // Premier usage probable (pas encore de pull, ou profil insuffisant) : empty state
  // pédagogique sans le filtre période — l'utilisateur doit d'abord lancer un pull.
  if (isEmpty && !isFiltered) {
    return (
      <div className="space-y-4 max-w-2xl mx-auto">
        <EmptyStateNoProfile />
        <Card className="p-4 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Si vous avez déjà configuré votre profil, lancez une première recherche
            d'AO sur BOAMP + TED filtrée par votre périmètre métier.
          </p>
          <Button
            variant="default"
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
          >
            {recompute.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Lancer une recherche d'AO maintenant
          </Button>
        </Card>
      </div>
    );
  }

  const header = (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-primary/10 text-primary">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">Opportunités suggérées</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {stats?.total ?? 0} suggestion{(stats?.total ?? 0) > 1 ? 's' : ''} classée
              {(stats?.total ?? 0) > 1 ? 's' : ''} par pertinence
              {stats && stats.total > 0 && (
                <>
                  {' '}
                  · <span className="text-emerald-700 font-medium">{stats.strong}</span> forte
                  {stats.strong > 1 ? 's' : ''}, <span className="text-amber-700 font-medium">{stats.moderate}</span> modérée
                  {stats.moderate > 1 ? 's' : ''}, <span className="text-rose-700 font-medium">{stats.weak}</span> faible
                  {stats.weak > 1 ? 's' : ''}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <PeriodFilter value={periodDays} onChange={setPeriodDays} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
          >
            {recompute.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Recalculer mes suggestions
          </Button>
        </div>
      </div>
    </Card>
  );

  // Cas filtré-vide : on garde le header avec le filtre pour que l'utilisateur
  // puisse élargir la période, et on affiche un message dans le corps.
  if (isEmpty) {
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        {header}
        <Card className="p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Aucune opportunité sur les {periodDays} derniers jours. Élargissez la période
            ou lancez une nouvelle recherche.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {header}

      {/* Section Forte */}
      <SectionDivider tone="strong" count={grouped.strong.length}>
        {grouped.strong.map((ao) => (
          <SuggestedOpportunityCard key={ao.id} ao={ao} />
        ))}
      </SectionDivider>

      {/* Section Modérée */}
      <SectionDivider tone="moderate" count={grouped.moderate.length}>
        {grouped.moderate.map((ao) => (
          <SuggestedOpportunityCard key={ao.id} ao={ao} />
        ))}
      </SectionDivider>

      {/* Section Faible — repliable par défaut */}
      <SectionDivider tone="weak" count={grouped.weak.length} defaultCollapsed>
        {grouped.weak.map((ao) => (
          <SuggestedOpportunityCard key={ao.id} ao={ao} />
        ))}
      </SectionDivider>
    </div>
  );
}
